import _ from 'lodash'
import React from 'react'
import Steps from 'antd/es/steps'
import 'antd/es/steps/style'
import classNames from 'classnames'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { Button } from 'browser/components/atomic-elements/atoms/button/button'
import { EntityErrorBlock } from 'browser/components/atomic-elements/atoms/error-block/entity-error-block'
import { Footer } from 'browser/components/atomic-elements/atoms/footer/footer'
import { evaluateExpressionWithScopes } from 'shared-libs/helpers/evaluation'
import { CustomFormulas } from 'shared-libs/helpers/formulas'
import { buildErrorTree } from 'shared-libs/components/entity/helper'
import { UISchemaResolver } from 'shared-libs/resolvers/ui-schema-resolver'

import 'browser/components/atomic-elements/atoms/form-wizard/_form-wizard.scss'

interface IFormWizardItem {
  /* formula string similar to ui:conditional to control the visibility of this item */
  test?: string
  title: string
  /* ui schema view for this item */
  view: any
}

/**
 * A multi-step form wizard to break a large form into smaller ones.
 *
 * @uiComponent
 */
interface IFormWizardProps extends IBaseProps {
  frames: any
  /** Form list; left raw by the renderer (recognised `items` keyword) so each `view` renders lazily. */
  items: IFormWizardItem[]
  /** Fixed body height so the footer doesn't jump between forms. Number is px; string is verbatim (e.g. "40vh"). */
  fixedHeight?: number | string
  entity?: any
  errors?: any
  onCancel?: () => void
  onSave?: () => Promise<any> | void
}

interface IFormWizardState {
  currentIndex: number
  isSaving: boolean
  stepError: string | null
  // Current form's validation errors, keyed by valuePath.
  stepErrors: Record<string, any>
  // Header jumps unlocked once the final form is reached (every form has passed forward validation).
  allowStepNavigation: boolean
}

export class FormWizard extends React.Component<IFormWizardProps, IFormWizardState> {
  private isUnmounted = false

  public state: IFormWizardState = {
    currentIndex: 0,
    isSaving: false,
    stepError: null,
    stepErrors: {},
    allowStepNavigation: false,
  }

  public componentWillUnmount() {
    this.isUnmounted = true
  }

  public render() {
    const { className } = this.props
    const items = this.getVisibleItems()
    if (_.isEmpty(items)) {
      return null
    }
    const currentIndex = this.getCurrentIndex(items)
    const currrentItem = items[currentIndex]
    const isLast = currentIndex === items.length - 1

    return (
      <div className={classNames('c-formWizard', className)}>
        {this.renderProgressHeader(items, currentIndex)}
        {this.renderBody(currrentItem, currentIndex, items.length)}
        {this.renderFooter(currentIndex, isLast)}
      </div>
    )
  }

  private renderProgressHeader(
    visible: Array<{ item: IFormWizardItem; originalIndex: number }>,
    currentIndex: number,
  ) {
    return (
      <div className='c-formWizard-header'>
        <Steps
          className='vector-progress-steps'
          size='small'
          current={currentIndex}
          onChange={this.state.allowStepNavigation ? this.handleStepClick : undefined}
          items={visible.map((entry, idx) => ({
            title: entry.item.title,
            key: entry.item.title || idx,
          }))}
        />
      </div>
    )
  }

  private renderBody(
    body: { item: IFormWizardItem; originalIndex: number },
    currentIndex: number,
    total: number,
  ) {
    const { frames, fixedHeight } = this.props
    const renderer = frames.getContext('renderer')

    let form: React.ReactNode = null
    if (renderer && body.item.view) {
      const uiSchema = body.item.view
      const uiSchemaPath = frames
        .getContext('uiSchemaPath')
        .concat(['items', body.originalIndex, 'view'])
      // Expose goNext/goBack/save on the frame stack so a form's own content can drive navigation.
      const stepState = [{
        stepIndex: currentIndex,
        isFirstStep: currentIndex === 0,
        isLastStep: currentIndex === total - 1,
        actionHooks: {
          goNext: this.handleNext,
          goBack: this.handleBack,
          cancel: this.props.onCancel,
          save: this.handleSave,
        },
      }]
      const newFrames = renderer.createChildFrame(frames, { uiSchema, uiSchemaPath }, stepState)
      form = renderer.createElementFromFrame(newFrames)
    }

    return (
      <div
        className='c-formWizard-body'
        style={fixedHeight ? { height: fixedHeight } : undefined}
      >
        {form}
      </div>
    )
  }

  private renderFooter(currentIndex: number, isLast: boolean) {
    const { onCancel, entity, errors } = this.props
    const { isSaving, stepError, stepErrors } = this.state

    const displayErrors = !_.isEmpty(stepErrors)
      ? stepErrors
      : (isLast ? errors : undefined)
    const errorBlockChildren = !_.isEmpty(displayErrors)
      ? (
        <div className='c-formWizard-footerErrors'>
          {stepError && <div className='c-formWizard-footerErrors-lead'>{stepError}</div>}
          <EntityErrorBlock entity={entity} errors={displayErrors} />
        </div>
      )
      : undefined

    return (
      <Footer
        className='c-formWizard-footer'
        shouldAnimate={false}
        onCancelButtonClick={onCancel}
        cancelButtonText='Cancel'
        isCancelButtonDisabled={isSaving}
        primaryButtonText={isLast ? 'Save' : 'Next'}
        onPrimaryButtonClick={isLast ? this.handleSave : this.handleNext}
        isPrimaryButtonLoading={isSaving}
        primaryButtonGroupElement={this.renderBackButton(currentIndex)}
        errorText={stepError || undefined}
        errorBlockChildren={errorBlockChildren}
      />
    )
  }

  private renderBackButton(currentIndex: number) {
    if (currentIndex === 0) {
      return undefined
    }
    return (
      <div className='c-footer-item'>
        <Button
          data-debug-id='formWizardBackButton'
          isDisabled={this.state.isSaving}
          onClick={this.handleBack}
        >
          Back
        </Button>
      </div>
    )
  }

  private handleBack = () => {
    this.clearFieldHighlights()
    this.setState((prev) => ({
      currentIndex: Math.max(prev.currentIndex - 1, 0),
      stepError: null,
      stepErrors: {},
    }))
  }

  private handleNext = () => {
    const visible = this.getVisibleItems()
    const currentIndex = this.getCurrentIndex(visible)
    const currentItem = visible[currentIndex]
    const paths = UISchemaResolver.getValuePaths(currentItem.item.view, this.props.entity)

    void Promise.resolve(this.props.entity?.validate()).then((validationErrors) => {
      if (this.isUnmounted) {
        return
      }
      // filter for errors revelant for the current step only
      const stepErrors = this.stepErrorsFor(paths, validationErrors)
      if (!_.isEmpty(stepErrors)) {
        this.setFieldHighlights(stepErrors)
        this.setState({
          stepError: 'Complete the required fields on this step before continuing.',
          stepErrors,
        })
      } else {
        this.clearFieldHighlights()
        this.setState((prev) => {
          const next = Math.min(prev.currentIndex + 1, visible.length - 1)
          return {
            currentIndex: next,
            stepError: null,
            stepErrors: {},
            allowStepNavigation: prev.allowStepNavigation || next === visible.length - 1,
          }
        })
      }
    }).catch((error: any) => {
      if (!this.isUnmounted) {
        this.setState({ stepError: this.saveErrorMessage(error) })
      }
    })
  }

  // Seed the shared renderer's errorsTree so invalid fields show their red state inline.
  private setFieldHighlights(stepErrors: Record<string, any>) {
    const renderer = this.props.frames.getContext('renderer')
    renderer?.setStateAtPath?.('errorsTree', buildErrorTree(stepErrors))
  }

  private clearFieldHighlights() {
    const renderer = this.props.frames.getContext('renderer')
    renderer?.setStateAtPath?.('errorsTree', {})
  }

  private handleStepClick = (index: number) => {
    this.clearFieldHighlights()
    this.setState({ currentIndex: index, stepError: null, stepErrors: {} })
  }

  private handleSave = () => {
    const { onSave } = this.props
    if (!onSave) {
      return
    }
    this.setState({ isSaving: true, stepError: null })
    return Promise.resolve(onSave())
      .then(() => {
        if (!this.isUnmounted) {
          this.setState({ isSaving: false })
        }
      })
      .catch((error: any) => {
        if (!this.isUnmounted) {
          // Field/validation errors flow back via props.errors; surface a hard failure here.
          this.setState({ isSaving: false, stepError: this.saveErrorMessage(error) })
        }
      })
  }

  private saveErrorMessage(error: any): string {
    const message = error?.message
    return _.isString(message) && message.trim() ? message : 'Could not save. Please try again.'
  }

  private getVisibleItems(): Array<{ item: IFormWizardItem; originalIndex: number }> {
    const { items = [] } = this.props
    return items
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => this.isVisible(item))
  }

  private isVisible(item: IFormWizardItem): boolean {
    if (item.test == null || item.test === '') {
      return true
    }
    try {
      return !!evaluateExpressionWithScopes(this.props.frames, String(item.test), { ...CustomFormulas })
    } catch (e) {
      // A malformed test shouldn't hide a form and strand the user.
      console.error('Error evaluating visibility test:', e, item)
      return true
    }
  }

  private getCurrentIndex(visible = this.getVisibleItems()): number {
    return _.clamp(this.state.currentIndex, 0, Math.max(visible.length - 1, 0))
  }

  // Returns the errors whose value-path belongs to this form (exact or nested).
  private stepErrorsFor(paths: Set<string>, errors: any): Record<string, any> {
    if (_.isEmpty(errors) || paths.size === 0) {
      return {}
    }
    const pathList = Array.from(paths)
    return _.pickBy(errors, (_msgs, key) => {
      if (!key || key === 'undefined') {
        return false
      }
      return pathList.some((p) => key === p || key.startsWith(`${p}.`) || p.startsWith(`${key}.`))
    })
  }
}
